import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Table, Tag, Select, DatePicker, Button, Input, Drawer, Card, Row, Col, Space, Divider, Pagination } from "antd";
import { FilePdfOutlined, FilterOutlined, SearchOutlined, MenuOutlined } from "@ant-design/icons";
import DashboardLayout from "../layout/index";
import PaymentModeModal from "../PatientRegister/PaymentModeModal";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { format } from "date-fns";

const { Option } = Select;
const { Search } = Input;

// map your raw serviceType values to the labels you want (keys are UPPERCASE)
const serviceTypeLabels = {
  // CONSULTATION: "Consultation",
  PHARMACY: "Pharmacy",
  PATHOLOGY: "Pathology",
  LAB: "Pathology",
  RADIOLOGY: "Radiology",
  SURGERY: "Surgery",
  BOOK_SERVICE: "CureBay Service",
};

// Ensure filter always shows these service types regardless of current results
const ALL_SERVICE_TYPES = [
  "CONSULTATION",
  "PHARMACY",
  "PATHOLOGY",
  "RADIOLOGY",
];

const normalizeServiceType = (value) =>
  (value ? String(value).toUpperCase().trim() : "");

const toTitleCase = (value) => {
  if (!value) return "—";
  const cleaned = String(value).replace(/_/g, " ");
  return cleaned
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

export default function PaymentDetails() {
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [focusedInput, setFocusedInput] = useState(null);
  // Pagination state
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
    totalElements: 0
  });

  // Responsive breakpoints
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth >= 768 && window.innerWidth < 1024);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const [searchFirstName, setSearchFirstName] = useState("");
  const [searchFirstNameInput, setSearchFirstNameInput] = useState("");
  // Filters & pagination
  const [statusFilter, setStatusFilter] = useState(null);

  const [dateRange, setDateRange] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [serviceTypeFilter, setServiceTypeFilter] = useState(null);

  // Mobile/Tablet filter drawer
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  // Modal & payment state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPaymentItem, setSelectedPaymentItem] = useState(null);
  const [paymentMode, setPaymentMode] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [invoiceURL, setInvoiceURL] = useState("");
  const disabledFeatures = JSON.parse(localStorage.getItem("disabledFeatures") || "[]");

  const API_URL = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("auth_token");
  const tenantId = localStorage.getItem("tenantId");
  const role = localStorage.getItem("role");

  // Handle window resize with tablet breakpoint
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
      setIsDesktop(width >= 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  console.log("Selected payment item:", selectedPaymentItem);
  const toDateParam = (isoDate) => {
    const d = new Date(isoDate);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    async function fetchOrders() {
      setLoading(true);
      setFilterLoading(true);
      const clinicId = localStorage.getItem("clinicId");
      let url = `${API_URL}orders/get-all?clinicId=${clinicId}&pageNumber=${pagination.current}&pageSize=${pagination.pageSize}`;

      if (dateRange[0] && dateRange[1]) {
        const fromDate = toDateParam(dateRange[0]);
        const toDate = toDateParam(dateRange[1]);
        url += `&fromDate=${fromDate}&toDate=${toDate}`;
      }

      // Add status filter if selected
      if (statusFilter) {
        url += `&status=${statusFilter}`;
      }

      // Add service type filter if selected
      if (serviceTypeFilter) {
        url += `&serviceType=${normalizeServiceType(serviceTypeFilter)}`;
      }

      // Add search text if provided
      if (searchFirstName) {
        url += `&search=${encodeURIComponent(searchFirstName)}`;
      }

      try {
        const resp = await axios.get(url, {
          headers: {
            Accept: "*/*",
            Authorization: `Bearer ${token}`,
            tenantId: tenantId,
          },
        });

        const responseData = resp.data.data;
        setOrders(responseData.content || []);

        // Update pagination state with server response
        setPagination(prev => ({
          ...prev,
          total: responseData.totalElements || 0,
          totalPages: responseData.totalPages || 0,
          totalElements: responseData.totalElements || 0
        }));

        console.log(responseData.content, "RDETFGYUHIJ")
      } catch (err) {
        console.error("Fetch error:", err.response || err);
        toast.error("Failed to fetch payment details");
      } finally {
        setLoading(false);
        setFilterLoading(false);
      }
    }

    if (token) fetchOrders();
  }, [token, API_URL, dateRange, pagination.current, pagination.pageSize, statusFilter, serviceTypeFilter, searchText,searchFirstName]);

  // Handle pagination change
  const handlePaginationChange = (page, size) => {
    setPagination(prev => ({
      ...prev,
      current: page,
      pageSize: size || prev.pageSize
    }));
  };

  // Debounced search function
  const debouncedSearch = useCallback(
    (() => {
      let timeoutId;
      return (value) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          setSearchText(value);
          setPagination(prev => ({ ...prev, current: 1 }));
        }, 500);
      };
    })(),
    []
  );

  // Debounce searchFirstNameInput -> searchFirstName to trigger API only after typing stops
  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchFirstName(searchFirstNameInput);
      setPagination(prev => ({ ...prev, current: 1 }));
    }, 500);
    return () => clearTimeout(handler);
  }, [searchFirstNameInput]);

  // Handle filter changes - reset to first page
  const handleFilterChange = (filterType, value) => {
    setPagination(prev => ({ ...prev, current: 1 }));

    switch (filterType) {
      case 'status':
        setStatusFilter(value);
        break;
      case 'serviceType':
        setServiceTypeFilter(value);
        break;
      case 'search':
        setSearchText(value);
        break;
      case 'dateRange':
        setDateRange(value);
        break;
      default:
        break;
    }
  };

  // get all distinct service types present from API response
  const serviceTypes = Array.from(
    new Set([
      ...ALL_SERVICE_TYPES,
      ...orders
        .map((o) => normalizeServiceType(o.serviceType))
        .filter((st) => !!st),
    ])
  );

  // Filter service types based on feature flags
  const filteredServiceTypes = disabledFeatures.includes("schedule-service")
    ? serviceTypes.filter(
        (st) => !["PHARMACY", "PATHOLOGY", "RADIOLOGY", "SURGERY"].includes(normalizeServiceType(st))
      )
    : serviceTypes;

  // common header style
  const headerStyle = {
    backgroundColor: "#F0FCFA",
    color: "#003A71",
    fontWeight: 600,
    textAlign: "center",
  };

  const handlePay = (record) => {
    setSelectedPaymentItem(record);
    setModalOpen(true);
  };

  const handleViewInvoice = async (record) => {
    try {
      setDownloadingInvoiceId(record.id);
      const resp = await axios.get(
        `${API_URL}orders/receipt/${record.id}`,
        {
          headers: {
            Accept: "*/*",
            Authorization: `Bearer ${token}`,
            tenantId: tenantId,
          },
        }
      );
      const b64 = resp.data.data;
      const binStr = atob(b64);
      const len = binStr.length;
      const buf = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        buf[i] = binStr.charCodeAt(i);
      }
      const blob = new Blob([buf], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setInvoiceURL(url);
      setInvoiceModalOpen(true);
    } catch (err) {
      toast.error("Failed to preview invoice.");
      console.error("Could not fetch invoice:", err);
    } finally {
      setDownloadingInvoiceId(null);
    }
  };

  // Handle export to Excel
  const handleExportToExcel = async () => {
    try {
      setExporting(true);
      
      // Get current date range or default to last 30 days
      let fromDate, toDate;
      if (dateRange[0] && dateRange[1]) {
        fromDate = toDateParam(dateRange[0]);
        toDate = toDateParam(dateRange[1]);
      } else {
        // Default to last 30 days if no date range selected
        const today = new Date();
        const thirtyDaysAgo = new Date(today.getTime() - (30 * 24 * 60 * 60 * 1000));
        fromDate = toDateParam(thirtyDaysAgo);
        toDate = toDateParam(today);
      }

      const exportUrl = `${API_URL}reports/orders.csv?fromDate=${fromDate}&toDate=${toDate}`;
      
      // Create a temporary link to download the file
      const link = document.createElement('a');
      link.href = exportUrl;
      link.download = `payment-details-${fromDate}-to-${toDate}.csv`;
      
      // Add authorization headers by creating a fetch request
      const response = await fetch(exportUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'tenantId': tenantId,
        },
      });

      if (!response.ok) {
        throw new Error('Export failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      link.href = url;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Export completed successfully!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export data. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  // Desktop table columns (full width)
  const desktopColumns = [
    {
      title: "Appointment ID",
      dataIndex: "appointmentId",
      key: "appointmentId",
      sorter: (a, b) => a.appointmentId - b.appointmentId,
      width: 130,
      align: "center",
      onHeaderCell: () => ({ style: headerStyle }),
    },
    {
      title: "Patient",
      dataIndex: "patientName",
      key: "patientName",
      width: 150,
      align: "center",
      onHeaderCell: () => ({ style: headerStyle }),
    },
    {
      title: "Service Type",
      dataIndex: "serviceType",
      key: "serviceType",
      width: 220,
      align: "center",
      onHeaderCell: () => ({ style: headerStyle }),
      render: (text, record) => {
        const key = normalizeServiceType(text);
        const serviceTypeLabel = serviceTypeLabels[key] || toTitleCase(key);
        
        // If it's a consultation, show appointment details
        if (key === "CONSULTATION" && record.appoimentDetails && record.appoimentDetails.length > 0) {
          const appointment = record.appoimentDetails[0];
          return (
            <div className="text-center py-2">
              <div className="font-semibold text-sm text-gray-800 mb-1">{serviceTypeLabel}</div>
              <div className="bg-blue-50 rounded-md px-2 py-1 border border-blue-200">
                <div className="text-xs font-medium text-blue-700">{appointment.whenAppointment}</div>
                <div className="text-xs text-blue-600 mt-0.5">{appointment.fromTime} - {appointment.toTime}</div>
              </div>
            </div>
          );
        }
        
        return (
          <div className="font-semibold text-sm text-gray-800">
            {serviceTypeLabel}
          </div>
        );
      },
    },
    {
      title: "Mobile",
      dataIndex: "patientMobileNo",
      key: "patientMobileNo",
      width: 130,
      align: "center",
      onHeaderCell: () => ({ style: headerStyle }),
    },
    {
      title: "Actual Amount",
      dataIndex: "amount",
      key: "amount",
      render: (amt) => <span className="font-semibold text-green-600">₹{amt.toFixed(2)}</span>,
      width: 110,
      align: "center",
      onHeaderCell: () => ({ style: headerStyle }),
    },
    {
      title: "Discounted Amount",
      dataIndex: "discountedAmount",
      key: "discountedAmount",
      render: (amt) => <span className="font-semibold text-green-600">₹{amt.toFixed(2)}</span>,
      width: 110,
      align: "center",
      onHeaderCell: () => ({ style: headerStyle }),
    },
    {
      title: "Status",
      dataIndex: "paymetStatus",
      key: "paymetStatus",
      filters: [
        { text: "Initiated", value: "INITIATED" },
        { text: "Completed", value: "COMPLETED" },
        { text: "Failed", value: "FAIL" },
      ],
      onFilter: (val, rec) => rec.paymetStatus === val,
      render: (s) => {
        const color =
          s === "COMPLETED" ? "green" : s === "FAIL" ? "volcano" : "orange";
        return <Tag color={color}>{s}</Tag>;
      },
      width: 120,
      align: "center",
      onHeaderCell: () => ({ style: headerStyle }),
    },
    {
      title: "Date",
      dataIndex: "modifiedDate",
      key: "modifiedDate",
      render: (d) => (d ? dayjs(d).format("DD MMM YYYY") : "—"),
      sorter: (a, b) =>
        new Date(a.modifiedDate || 0) - new Date(b.modifiedDate || 0),
      width: 120,
      align: "center",
      onHeaderCell: () => ({ style: headerStyle }),
    },
    {
      title: "Action",
      key: "action",
      width: 100,
      align: "center",
      onHeaderCell: () => ({ style: headerStyle }),
      render: (_, record) =>
        ["INITIATED", "FAIL"].includes(record.paymetStatus) ? (
          role === "USER" ? (
            <Button
              size="small"
              style={{
                backgroundColor: "#00C199",
                borderColor: "#00C199",
                color: "#fff",
              }}
              onClick={() => handlePay(record)}
            >
              Pay
            </Button>
          ) : (
            <p className="font-semibold text-sm ant-tag ant-tag-orange css-dev-only-do-not-override-pjilya">
              UnPaid
            </p>
          )
        ) : 
        ""
        // (
        //   <Button
        //     size="small"
        //     icon={<FilePdfOutlined />}
        //     loading={downloadingInvoiceId === record.id}
        //     style={{
        //       backgroundColor: "transparent",
        //       borderColor: "#003A71",
        //       color: "#003A71",
        //     }}
        //     onClick={() => handleViewInvoice(record)}
        //   >
        //     Invoice
        //   </Button>
        // ),
    },
  ];

  // Tablet Card View (similar to mobile but optimized for larger screen)
  const TabletCardView = ({ record }) => (
    <Card className="mb-4 shadow-sm hover:shadow-md transition-shadow" size="small">
      <Row gutter={[16, 8]}>
        <Col span={12}>
          <div>
            <div className="font-semibold text-lg mb-1">{record.patientName}</div>
            <div className="text-sm text-gray-600 mb-1">
              <strong>ID:</strong> {record.appointmentId}
            </div>
            <div className="text-sm text-gray-600">
              <strong>Mobile:</strong> {record.patientMobileNo}
            </div>
          </div>
        </Col>
        <Col span={12} className="text-right">
          <div className="font-bold text-green-600 text-xl mb-2">₹{record.amount.toFixed(2)}</div>
          <Tag
            color={record.paymetStatus === "COMPLETED" ? "green" : record.paymetStatus === "FAIL" ? "volcano" : "orange"}
            className="mb-2"
          >
            {record.paymetStatus}
          </Tag>
        </Col>
      </Row>

      <Divider className="my-3" />

      <Row gutter={[16, 8]} className="mb-3">
        <Col span={12}>
          <div className="text-sm">
            <strong>Service:</strong> {(() => {
              const key = normalizeServiceType(record.serviceType);
              const serviceTypeLabel = serviceTypeLabels[key] || toTitleCase(record.serviceType);
              
              if (key === "CONSULTATION" && record.appoimentDetails && record.appoimentDetails.length > 0) {
                const appointment = record.appoimentDetails[0];
                return (
                  <div className="mt-1">
                    <div className="font-semibold text-gray-800">{serviceTypeLabel}</div>
                    <div className="bg-blue-50 rounded-md px-2 py-1 border border-blue-200 mt-1 inline-block">
                      <div className="text-xs font-medium text-blue-700">{appointment.whenAppointment}</div>
                      <div className="text-xs text-blue-600">{appointment.fromTime} - {appointment.toTime}</div>
                    </div>
                  </div>
                );
              }
              
              return <span className="font-semibold text-gray-800">{serviceTypeLabel}</span>;
            })()}
          </div>
        </Col>
        <Col span={12} className="text-right">
          <div className="text-sm text-gray-600">
            <strong>Date:</strong> {record.modifiedDate ? dayjs(record.modifiedDate).format("DD MMM YYYY") : "—"}
          </div>
        </Col>
      </Row>

      <div className="pt-2">
        {["INITIATED", "FAIL"].includes(record.paymetStatus) ? (
          role === "USER" ? (
            <Button
              block
              size="large"
              className="bg-gradient-to-r from-[#004172] to-[#3cba84]  font-medium text-white h-[35px] text-[16px] "
              onClick={() => handlePay(record)}
            >
              Pay Now
            </Button>
          ) : (
            <Tag color="orange" className="w-full text-center py-2">UnPaid</Tag>
          )
        ) : (
          <Button
            block
            size="large"
            icon={<FilePdfOutlined style={{ fontSize: "16px" }} />}
            loading={downloadingInvoiceId === record.id}
            style={{
              backgroundColor: "transparent",
              borderColor: "#003A71",
              color: "#003A71",
              height: "35px",
              fontSize: "16px",
              fontWeight: "600"
            }}
            onClick={() => handleViewInvoice(record)}
          >
            View Invoice
          </Button>
        )}
      </div>
    </Card>
  );

  // Mobile table columns (very minimal)
  const mobileColumns = [
    {
      title: "Patient",
      dataIndex: "patientName",
      key: "patientName",
      width: "40%",
      onHeaderCell: () => ({ style: headerStyle }),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (amt) => <span className="font-semibold text-green-600">₹{amt.toFixed(2)}</span>,
      width: "30%",
      align: "center",
      onHeaderCell: () => ({ style: headerStyle }),
    },
    {
      title: "Action",
      key: "action",
      width: "30%",
      align: "center",
      onHeaderCell: () => ({ style: headerStyle }),
      render: (_, record) =>
        ["INITIATED", "FAIL"].includes(record.paymetStatus) ? (
          role === "USER" ? (
            <Button
              size="small"
              style={{
                backgroundColor: "#00C199",
                borderColor: "#00C199",
                color: "#fff",
              }}
              onClick={() => handlePay(record)}
            >
              Pay
            </Button>
          ) : (
            <Tag color="orange">UnPaid</Tag>
          )
        ) : (
          <Button
            size="small"
            icon={<FilePdfOutlined />}
            loading={downloadingInvoiceId === record.id}
            style={{
              backgroundColor: "transparent",
              borderColor: "#003A71",
              color: "#003A71",
            }}
            onClick={() => handleViewInvoice(record)}
          />
        ),
    },
  ];

  // Since we're now doing server-side filtering, we don't need client-side filtering
  const filtered = orders;

  const FilterContent = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Search Patient
        </label>
        {/* <Search
          placeholder="Search by patient"
          allowClear
          value={searchText}
          onSearch={(val) => handleFilterChange('search', val.trim())}
          onChange={(e) => debouncedSearch(e.target.value)}
        /> */}

        <input
          id="searchFirstName"
          type="text"
          value={searchFirstNameInput}
          onChange={(e) => setSearchFirstNameInput(e.target.value)}
          onFocus={() => setFocusedInput("searchFirstName")}
          onBlur={() => setFocusedInput(null)}
          placeholder="Search by name"
          className="w-full sm:w-56 border border-gray-300 rounded-md px-3 py-2 pr-8 text-sm focus:ring-2 focus:ring-[#004276] focus:border-transparent"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Payment Status
        </label>
        <Select
          value={statusFilter}
          placeholder="Filter by status"
          allowClear
          className="w-full"
          onChange={(val) => handleFilterChange('status', val)}
        >
          <Option value="INITIATED">Initiated</Option>
          <Option value="COMPLETED">Completed</Option>
          <Option value="FAIL">Failed</Option>
        </Select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Service Type
        </label>
        <Select
          value={serviceTypeFilter}
          placeholder="Filter by service type"
          allowClear
          className="w-full"
          onChange={(val) => handleFilterChange('serviceType', val)}
        >
          {filteredServiceTypes.map((st) => (
            <Option key={st} value={st}>
              {serviceTypeLabels[normalizeServiceType(st)] || toTitleCase(st)}
            </Option>
          ))}
        </Select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Date Range
        </label>
        <DatePicker.RangePicker
          value={dateRange}
          onChange={(vals) => handleFilterChange('dateRange', vals || [])}
          className="w-full"
        />
      </div>

      <div className="pt-4">
        <Button
          type="primary"
          icon={<FilePdfOutlined />}
          onClick={handleExportToExcel}
          loading={exporting}
          className="w-full bg-green-600 hover:bg-green-700 border-green-600 hover:border-green-700"
        >
          Export to Excel
        </Button>
      </div>
    </div>
  );

  // Mobile Card View
  const MobileCardView = ({ record }) => (
    <Card className="mb-3 shadow-sm" size="small">
      <div className="space-y-2">
        <div className="flex justify-between items-start">
          <div>
            <div className="font-semibold text-base">{record.patientName}</div>
            <div className="text-sm text-gray-600">ID: {record.appointmentId}</div>
          </div>
          <div className="text-right">
            <div className="font-bold text-green-600 text-lg">₹{record.amount.toFixed(2)}</div>
            <Tag
              color={record.paymetStatus === "COMPLETED" ? "green" : record.paymetStatus === "FAIL" ? "volcano" : "orange"}
              size="small"
            >
              {record.paymetStatus}
            </Tag>
          </div>
        </div>

        <Divider className="my-2" />

        <div className="flex justify-between text-sm">
          <div className="flex-1">
            <div><strong>Service:</strong> {(() => {
              const key = normalizeServiceType(record.serviceType);
              const serviceTypeLabel = serviceTypeLabels[key] || toTitleCase(record.serviceType);
              
              if (key === "CONSULTATION" && record.appoimentDetails && record.appoimentDetails.length > 0) {
                const appointment = record.appoimentDetails[0];
                return (
                  <div className="mt-1">
                    <div className="font-semibold text-gray-800">{serviceTypeLabel}</div>
                    <div className="bg-blue-50 rounded-md px-2 py-1 border border-blue-200 mt-1 inline-block">
                      <div className="text-xs font-medium text-blue-700">{appointment.whenAppointment}</div>
                      <div className="text-xs text-blue-600">{appointment.fromTime} - {appointment.toTime}</div>
                    </div>
                  </div>
                );
              }
              
              return <span className="font-semibold text-gray-800">{serviceTypeLabel}</span>;
            })()}</div>
          </div>
          <div className="text-right">
            <span><strong>Mobile:</strong> {record.patientMobileNo}</span>
          </div>
        </div>

        <div className="text-sm text-gray-600">
          <strong>Date:</strong> {record.modifiedDate ? dayjs(record.modifiedDate).format("DD MMM YYYY, hh:mm A") : "—"}
        </div>

        <div className="pt-2">
          {["INITIATED", "FAIL"].includes(record.paymetStatus) ? (
            role === "USER" ? (
              <Button
                block
                className="bg-gradient-to-r from-[#004172] to-[#3cba84]  font-medium text-white"
                onClick={() => handlePay(record)}
              >
                Pay Now
              </Button>
            ) : (
              <Tag color="orange" className="w-full text-center py-2">UnPaid</Tag>
            )
          ) : (
            <Button
              block
              icon={<FilePdfOutlined />}
              loading={downloadingInvoiceId === record.id}
              style={{
                backgroundColor: "transparent",
                borderColor: "#003A71",
                color: "#003A71",
              }}
              onClick={() => handleViewInvoice(record)}
            >
              View Invoice
            </Button>
          )}
        </div>
      </div>
    </Card>
  );

  // Get appropriate columns based on screen size (only used for desktop now)
  const getColumns = () => {
    return desktopColumns;
  };

  // Get appropriate scroll settings (only used for desktop now)
  const getScrollSettings = () => {
    return { x: 1000 };
  };
  console.log(dateRange, "dateRange")
  useEffect(() => {
    if (focusedInput) {
        const element = document.getElementById(focusedInput);
        if (element) {
            element.focus();
            // Set cursor position to end of input
            const length = element.value.length;
            element.setSelectionRange(length, length);
        }
    }
}, [orders, loading]); 
  return (
    <DashboardLayout>
      <div className="p-3 md:p-6 bg-white rounded-xl shadow-md">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
          <h2 className="text-xl md:text-2xl font-bold text-[#003A71] mb-2 sm:mb-0">
            Payment Details
          </h2>

          {/* Mobile & Tablet Filter Button */}
          {(isMobile || isTablet) && (
            <Button
              icon={<FilterOutlined />}
              onClick={() => setFilterDrawerOpen(true)}
              type="primary"
              style={{ backgroundColor: "#003A71" }}
            >
              Filters
            </Button>
          )}
        </div>

        {/* Desktop Filters */}
        {isDesktop && (
          <div className="flex flex-wrap gap-4 mb-6">

            {/* <Search
              placeholder="Search by patient"
              allowClear
              value={searchText}
              onSearch={(val) => handleFilterChange('search', val.trim())}
              onChange={(e) => debouncedSearch(e.target.value)}
              style={{ width: 240 }}
            /> */}

            <input
              id="searchFirstName"
              type="text"
              value={searchFirstNameInput}
              onChange={(e) => setSearchFirstNameInput(e.target.value)}
              onFocus={() => setFocusedInput("searchFirstName")}
              onBlur={() => setFocusedInput(null)}
              placeholder="Search by patient"
              className="w-full sm:w-56 border border-gray-300 rounded-md px-3 py-2 pr-8 text-sm focus:ring-2 focus:ring-[#004276] focus:border-transparent"
            />


            <Select
              value={statusFilter}
              placeholder="Filter by status"
              allowClear
              className="w-48"
              onChange={(val) => handleFilterChange('status', val)}
            >
              <Option value="INITIATED">Initiated</Option>
              <Option value="COMPLETED">Completed</Option>
              <Option value="FAIL">Failed</Option>
            </Select>

            <Select
              value={serviceTypeFilter}
              placeholder="Filter by service type"
              allowClear
              className="w-48"
              onChange={(val) => handleFilterChange('serviceType', val)}
            >
              {filteredServiceTypes.map((st) => (
                <Option key={st} value={st}>
                  {serviceTypeLabels[normalizeServiceType(st)] || toTitleCase(st)}
                </Option>
              ))}
            </Select>

            <DatePicker.RangePicker
              value={dateRange}
              onChange={(vals) => handleFilterChange('dateRange', vals || [])}
              className="w-72"
            />

            <Button
              type="primary"
              icon={<FilePdfOutlined />}
              onClick={handleExportToExcel}
              loading={exporting}
              className="bg-green-600 hover:bg-green-700 border-green-600 hover:border-green-700"
            >
              Export to Excel
            </Button>
          </div>
        )}

        {/* Mobile & Tablet Filter Drawer */}
        <Drawer
          title="Filter Options"
          placement="bottom"
          height="auto"
          open={filterDrawerOpen}
          onClose={() => setFilterDrawerOpen(false)}
          className="lg:hidden"
        >
          <FilterContent />
        </Drawer>

        {/* Results Summary */}
        <div className="mb-4 text-sm text-gray-600">
          {filterLoading ? (
            <span>Loading...</span>
          ) : (
            `Showing ${((pagination.current - 1) * pagination.pageSize) + 1} to ${Math.min(pagination.current * pagination.pageSize, pagination.totalElements)} of ${pagination.totalElements} records`
          )}
        </div>

        {/* Content - Cards for Mobile/Tablet, Table for Desktop */}
        {isMobile ? (
          <div>
            {loading ? (
              <div className="text-center py-8">Loading...</div>
            ) : filtered.length > 0 ? (
              <div>
                {filtered.map((record) => (
                  <MobileCardView key={record.id} record={record} />
                ))}

                {/* Mobile Pagination */}
                <div className="mt-6 flex justify-center">
                  <Pagination
                    current={pagination.current}
                    total={pagination.totalElements}
                    pageSize={pagination.pageSize}
                    onChange={handlePaginationChange}
                    showSizeChanger={false}
                    showQuickJumper={false}
                    showTotal={(total, range) => `${range[0]}-${range[1]} of ${total}`}
                    size="small"
                  />
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                {pagination.totalElements > 0 ? 'No records found for the current filters' : 'No records found'}
              </div>
            )}
          </div>
        ) : isTablet ? (
          <div>
            {loading ? (
              <div className="text-center py-8">Loading...</div>
            ) : filtered.length > 0 ? (
              <div>
                <Row gutter={[16, 0]}>
                  {filtered.map((record) => (
                    <Col key={record.id} span={12}>
                      <TabletCardView record={record} />
                    </Col>
                  ))}
                </Row>

                {/* Tablet Pagination */}
                <div className="mt-6 flex justify-center">
                  <Pagination
                    current={pagination.current}
                    total={pagination.totalElements}
                    pageSize={pagination.pageSize}
                    onChange={handlePaginationChange}
                    showSizeChanger={true}
                    showQuickJumper={true}
                    showTotal={(total, range) => `${range[0]}-${range[1]} of ${total}`}
                    pageSizeOptions={['10', '20', '50']}
                  />
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                {pagination.totalElements > 0 ? 'No records found for the current filters' : 'No records found'}
              </div>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table
              rowKey="id"
              loading={loading}
              columns={desktopColumns}
              dataSource={filtered}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.totalElements,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "50", "100"],
                onChange: handlePaginationChange,
                onShowSizeChange: (_, size) => handlePaginationChange(1, size),
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} items`,
                responsive: true,
              }}
              bordered
              size="middle"
              className="overflow-hidden rounded-lg"
              scroll={{ x: 1000 }}
            />
          </div>
        )}

        {modalOpen && (
          <PaymentModeModal
            isOpen={modalOpen}
            onClose={() => setModalOpen(false)}
            onPay={(mode) => {
              setIsLoggingIn(true);
              setPaymentMode(mode);
              setIsLoggingIn(false);
              setModalOpen(false);
            }}
            setPaymentMode={setPaymentMode}
            isLoggingIn={isLoggingIn}
            paymentMode={paymentMode}
            paymentItem={
              selectedPaymentItem?.serviceType === "CONSULTATION"
                ? selectedPaymentItem?.appointmentObjectId
                : selectedPaymentItem?.id
            }
            paymenttype={
              selectedPaymentItem?.serviceType === "CONSULTATION"
                ? "Appointment"
                : "Order"
            }
          />
        )}

        {/* Responsive Invoice Modal */}
        {invoiceModalOpen && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-2 md:p-4">
            <div className="bg-white w-full max-w-6xl h-full max-h-[95vh] rounded-lg shadow-lg overflow-hidden relative">
              <div className="flex justify-between items-center p-3 md:p-4 border-b">
                <h2 className="text-lg md:text-xl font-semibold text-[#003A71]">
                  Invoice Preview
                </h2>
                <button
                  className="text-gray-500 hover:text-red-500 text-2xl font-bold"
                  onClick={() => {
                    setInvoiceModalOpen(false);
                    URL.revokeObjectURL(invoiceURL);
                  }}
                >
                  &times;
                </button>
              </div>
              <div className="h-[calc(100%-60px)] overflow-auto p-1 md:p-2">
                {invoiceURL && (
                  <iframe
                    src={invoiceURL}
                    title="Invoice PDF"
                    className="w-full h-full border-0"
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}